import { escape, BRAND, buildSponsorStrip } from "./reelShared";

/**
 * Instagram Story / Reel / TikTok — 1080×1920 (9:16 vertical).
 *
 * IMPORTANT safe-zone rules:
 *   • Top ~250px is covered by IG UI (username, close button)
 *   • Bottom ~350px is covered by IG UI (reactions, "send message")
 * Keep all critical content between y=280 and y=1580.
 */
export const buildStoryCardSVG = ({ user, color, tierShort, tierCap, memberNumber, sponsor, headline }) => {
    const w = 1080;
    const h = 1920;
    const safeTop = 280;
    const cx = w / 2;

    const hero = String(user.trees_restored);
    const heroLabel = "TREES RESTORED";
    const heroCaption = headline || "Because I drive with EVIQ.";

    const secondary = [
        { label: "OCEAN PLASTIC", value: `${user.ocean_plastic_lbs} LBS`, color: BRAND.ocean },
        { label: "BATTERIES PROTECTED", value: user.batteries_protected, color: BRAND.violet },
        { label: "CO₂ PREVENTED", value: `${user.co2_saved_lbs.toLocaleString()} LBS`, color: BRAND.solar },
        { label: "BATTERY LIFE EXTENDED", value: `${user.battery_life_extended_years} YRS`, color },
    ];
    const secSvg = secondary
        .map((m, i) => {
            const col = i % 2;
            const row = Math.floor(i / 2);
            const x = col * 460;
            const y = row * 220;
            return `
    <g transform="translate(${x} ${y})">
      <rect x="0" y="0" width="440" height="200" rx="16" fill="#0a0a0a" stroke="${BRAND.line}"/>
      <circle cx="26" cy="30" r="6" fill="${m.color}"/>
      <text x="42" y="38" fill="${BRAND.inkDim}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="18" font-weight="700" letter-spacing="4">${escape(m.label)}</text>
      <text x="26" y="140" fill="${BRAND.ink}" font-family="'Menlo', 'Courier New', monospace" font-size="72" font-weight="800">${escape(m.value)}</text>
    </g>`;
        })
        .join("");

    const sponsorSvg = sponsor ? buildSponsorStrip({ sponsor, cx, y: 1520, color }) : "";
    const bottomCaption = sponsor ? "" : `<text x="${cx}" y="1580" text-anchor="middle" fill="${BRAND.inkFaint}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="22" letter-spacing="7">POWERED BY THE EVIQ COMMUNITY</text>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="EVIQ Instagram story reel">
  <defs>
    <linearGradient id="st-bg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${BRAND.bgTop}"/>
      <stop offset="100%" stop-color="${BRAND.bgBottom}"/>
    </linearGradient>
    <radialGradient id="st-glow" cx="50%" cy="30%" r="70%">
      <stop offset="0%" stop-color="${color}" stop-opacity="0.22"/>
      <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#st-bg)"/>
  <rect width="${w}" height="${h}" fill="url(#st-glow)"/>

  <!-- Brand header -->
  <g transform="translate(80 ${safeTop})">
    <rect x="0" y="0" width="52" height="52" rx="10" fill="#0a0a0a" stroke="#27272a"/>
    <path d="M 16 12 L 32 12 L 26 28 L 36 28 L 20 48 L 26 30 L 14 30 Z" fill="${color}"/>
    <text x="72" y="22" fill="${BRAND.inkDim}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="16" font-weight="700" letter-spacing="6">EVIQ</text>
    <text x="72" y="46" fill="${BRAND.ink}" font-family="Georgia, serif" font-style="italic" font-size="26">Earth Initiative</text>
  </g>

  <!-- Tier chip -->
  <g transform="translate(${cx} ${safeTop + 130})">
    <rect x="-240" y="-32" width="480" height="64" rx="32" fill="${color}" fill-opacity="0.14" stroke="${color}"/>
    <text x="0" y="-6" text-anchor="middle" fill="${color}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="14" font-weight="700" letter-spacing="6">${escape(tierCap)}</text>
    <text x="0" y="20" text-anchor="middle" fill="${color}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="22" font-weight="800" letter-spacing="4">${escape(tierShort)} #${escape(memberNumber)}</text>
  </g>

  <!-- Headline -->
  <text x="${cx}" y="${safeTop + 260}" text-anchor="middle" fill="${BRAND.ink}" font-family="Georgia, serif" font-size="56" font-weight="700">${escape(heroCaption.split(".")[0] + ".")}</text>

  <!-- Hero metric -->
  <text x="${cx}" y="${safeTop + 380}" text-anchor="middle" fill="${BRAND.inkDim}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="20" font-weight="700" letter-spacing="8">${escape(heroLabel)}</text>
  <text x="${cx}" y="${safeTop + 610}" text-anchor="middle" fill="${BRAND.ink}" font-family="Georgia, 'Times New Roman', serif" font-size="240" font-weight="400" letter-spacing="-4">${escape(hero)}</text>

  <!-- Secondary metrics 2×2 grid -->
  <g transform="translate(80 ${safeTop + 700})">${secSvg}</g>

  ${sponsorSvg}
  ${bottomCaption}
</svg>`;
};
